import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Award, ShieldCheck, Sparkles, Droplet, CheckCircle, Leaf, HelpCircle, ArrowRight, Snowflake, X } from 'lucide-react';
import { benefits, applications, contactInfo } from '../data';
import SEO from '../components/SEO';

export default function Stenbelaegning() {
  const comparisonData = {
    columns: [
      { name: 'Egenskab', isHeader: true },
      { name: 'Helstøbt Stenbelægning', isFeatured: true },
      { name: 'OB-belægning' },
      { name: 'Beton' },
      { name: 'Betonflise' },
      { name: 'Asfalt' },
      { name: 'Løse sten' }
    ],
    rows: [
      { name: 'Natursten', values: [true, true, false, false, false, true] },
      { name: 'Farvestabil', values: [true, true, false, false, false, true] },
      { name: 'Permeabel / drænende', values: [true, false, false, false, false, true] },
      { name: 'Ukrudtshæmmende', values: [true, true, true, false, true, false] },
      { name: 'Frostsikkert', values: [true, true, false, true, true, true] },
      { name: 'Ingen synlige samlinger', values: [true, true, false, false, false, false] },
      { name: 'Uden snublefælde', values: [true, false, true, false, true, false] },
      { name: 'Skridsikker', values: [true, false, false, false, false, false] },
      { name: 'Spildresistent', values: [true, false, false, false, false, false] }
    ]
  };

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Helstøbt Stenbelægning til Indkørsel & Terrasse | Stenbelægning.dk"
        description="Læs om helstøbt stenbelægning fra Stenbelægning.dk. En stærk resinbunden naturstensbelægning uden fuger, løse sten eller ukrudt. Perfekt til indkørsler, terrasser og stier."
      />
      {/* Header Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              Premium Belægning
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-3">
              Helstøbt stenbelægning
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Fremtidens fugefrie og drænende belægning til private og erhverv
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Helstøbt stenbelægning er en moderne, eksklusiv løsning til dig, der ønsker en slidstærk, smuk og fuldstændig vedligeholdelsesnem overflade. Belægningen er velegnet til både nye anlæg og renovering af eksisterende belægninger.
            </p>
          </div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Hvor kan det anvendes?</h2>
            <p className="text-stone-500 mt-2">
              Vores belægning er ekstremt alsidig og kan lægges på næsten alle faste, stabile underlag.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.map((app, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-bold text-stone-900 mb-2 flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  {app.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive Section with Image */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">Hvad er helstøbt stenbelægning?</h2>
              <p className="text-stone-600 leading-relaxed">
                Belægningen består af naturlige småsten, der blandes grundigt med en højkvalitets, UV-resistent resin. Denne blanding udlægges i et jævnt lag direkte på et stabilt underlag.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Da belægningen udlægges i én sammenhængende arbejdsgang, opnår man en fuldstændig fugefri og glat overflade. Det ser ikke bare fantastisk ud, men løser også de klassiske problemer med ukrudt, flisepest og løse sten.
              </p>
              
              {/* 10 års garanti boks */}
              <div className="bg-stone-50 border border-stone-200 p-6 rounded-2xl space-y-6">
                <div className="flex gap-4 items-start">
                  <ShieldCheck className="h-6 w-6 text-stone-900 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-stone-900">10 års garanti på udført arbejde</h4>
                    <p className="text-sm text-stone-700 mt-1">
                      Vi står 100% inde for vores materialer og håndværk. Derfor yder vi en af branchens absolut bedste garantier på alt udført arbejde.
                    </p>
                  </div>
                </div>

                {/* De tre fordele under garantien */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-stone-200">
                  {/* Ingen ukrudt */}
                  <div className="flex items-center gap-3">
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0">
                      <Leaf className="h-5 w-5" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-0.5 bg-red-500 rotate-45 rounded-full"></div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-xs">Ingen ukrudt</h4>
                      <p className="text-[10px] text-stone-500">100% fugefri</p>
                    </div>
                  </div>

                  {/* Drænende løsning */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-600 shrink-0">
                      <Droplet className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-xs">Drænende</h4>
                      <p className="text-[10px] text-stone-500">Siver direkte igennem</p>
                    </div>
                  </div>

                  {/* Frostsikker */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-sky-50 text-sky-600 shrink-0">
                      <Snowflake className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-xs">Frostsikker</h4>
                      <p className="text-[10px] text-stone-500">Ingen frostsprængning</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative overflow-hidden shadow-xl rounded-2xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/badd6eea-b45e-42e2-bf84-1f92e8d91be3.jpg"
                alt="Terrasse med fugefri stenbelægning og havemøbler"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sammenligningstabel Section */}
      <section className="py-16 bg-stone-50 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
              Sammenligning
            </span>
            <h2 className="text-3xl font-bold text-stone-900 mt-3">Hvorfor vælge Helstøbt Stenbelægning?</h2>
            <p className="text-stone-500 mt-2">
              Se hvordan vores helstøbte løsning klarer sig sammenlignet med traditionelle belægningstyper.
            </p>
          </div>

          {/* Tabel Container */}
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50/75">
                    {comparisonData.columns.map((col, idx) => (
                      <th
                        key={idx}
                        className={`p-4 text-xs font-bold tracking-wider uppercase ${
                          col.isHeader
                            ? 'text-stone-500 w-1/4'
                            : col.isFeatured
                            ? 'text-center text-emerald-600 bg-emerald-50/50 border-x border-emerald-100'
                            : 'text-center text-stone-900'
                        }`}
                      >
                        {col.name}
                        {col.isFeatured && (
                          <span className="block text-[9px] text-emerald-600 font-extrabold tracking-widest mt-0.5">
                            (ANBEFALET)
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {comparisonData.rows.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-stone-50/50 transition-colors">
                      <td className="p-4 text-sm font-semibold text-stone-900">{row.name}</td>
                      {row.values.map((val, vIdx) => {
                        const isFeaturedCol = vIdx === 0;
                        return (
                          <td
                            key={vIdx}
                            className={`p-4 text-center ${
                              isFeaturedCol ? 'bg-emerald-50/20 border-x border-emerald-100/50' : ''
                            }`}
                          >
                            <div className="flex justify-center">
                              {val ? (
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700">
                                  <Check className="h-4 w-4 stroke-[3]" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-stone-100 text-stone-400">
                                  <X className="h-3.5 w-3.5 stroke-[2.5]" />
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Benefits List */}
      <section className="py-16 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative overflow-hidden shadow-xl rounded-2xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6055090a-18d2-4c2c-9d28-28de4d83c95a.jpg"
                alt="Fagmand der udlægger stenbelægning med spartel"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">De mange fordele</h2>
              <p className="text-stone-600">
                Når du vælger en helstøbt stenbelægning, får du en lang række unikke fordele, som traditionelle fliser, beton eller asfalt ikke kan matche:
              </p>

              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Eksklusivt udseende i natursten:</strong> Flot, luksuriøs og tidløs finish.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Skridsikker overflade:</strong> Meget sikker at færdes på, selv i vådt vejr.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Drænende løsning:</strong> Vandet trænger lynhurtigt igennem belægningen og ned i jorden.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Minimal vedligeholdelse:</strong> Kræver kun let fejning eller spuling efter behov.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Ukrudtsfri belægning:</strong> Ingen fuger betyder, at ukrudt ikke kan slå rødder.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Lang levetid:</strong> Meget modstandsdygtig over for frost, varme og UV-stråler.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Fugefri og uden samlinger:</strong> En helt glat, ubrudt og moderne overflade.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="text-stone-700"><strong>Mange farver og udtryk:</strong> Vælg mellem et væld af stentyper og nuancer.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-900 text-white py-16 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold">Vil du se, hvordan det ser ud i virkeligheden?</h2>
          <p className="text-stone-400 mt-4 max-w-2xl mx-auto">
            Besøg vores reference-side for at se billeder af udførte indkørsler, terrasser, trapper og gangarealer, eller se vores store udvalg af farver og materialer.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/vuba-materialer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Se farver og materialer
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et tilbud nu
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
